import { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Table, Tag, Dropdown } from 'antd'
import { useNavigate } from 'react-router';

// Constants
import { ROUTES, COMMON_FEATURES, TEMPLATE } from '../../../../../../constants'

// Components
import { EmptyIndicator } from '../../../../../../components/Indicator'
import { PrimaryButton } from '../../../../../../components/Decoration/Button';
import { Loading, ModalImage, ButtonLink, ModalStandard, TagLabel } from '../../../../../../components'

// Services
import { REDUX, TIME } from '../../../../../../services/util'

// Redux Actions
import * as REDUX_ACTIONS from '../../../../../..//services/redux/actions'

// Handler
import {
    table_handler,
} from '../../../../../../handlers'
import ExportExcelButton from '../../../../../../components/ExportExcelButton';
import SearchFields from './SearchFields';

const { VAM } = TEMPLATE
const { getColumnSearchProps, getColumnsFilterTag } = table_handler
const { parseTime } = TIME

const {
    VAM_ENTRY_STATUS_TITLE
} = VAM

const ServiceEntryPanel = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const style = useSelector(REDUX.reduxStoreWrapper('style'))
    const vamEntries = useSelector(REDUX.reduxStoreWrapper('vamEntries.entryByID'))
    const { itemByID: vamLines } = useSelector(REDUX.reduxStoreWrapper('vamLines'))
    const vehicles = useSelector(REDUX.reduxStoreWrapper('vehicles.staticsByID'))
    const { allVAMEIDs: vameids } = useSelector(REDUX.reduxStoreWrapper('vamEntries'))

    const [ dataSource, setDataSource ] = useState([])
    const [ isLoaded, setIsLoaded ] = useState(false);
    const [ rec, setRec ] = useState({})
    const [ isOpen, setIsOpen] = useState(false);
    
    const searchTextState = useState('')
    const searchedColumnState = useState('')

    const usePrevious = (value) => {
        const ref = useRef();
        useEffect(() => {
          ref.current = value;
        });
        return ref.current;
    }

    const prevVehicles = usePrevious (vamEntries);

    const ServiceEntryEditHDLR = (data) => {
        navigate(ROUTES.MODULE_ROUTE[COMMON_FEATURES.FEATURE_NAME.EDIT_SERVICE_ENTRY], { state : { data }})
    }

    const refreshDataSrc = () => {
        const newData = vameids &&
        vameids.map(vameid => {
            const vamEntry = vamEntries[vameid]
            const {
                vid
            } = vamEntry
            const vehicle = vehicles[vid]

            return {
                key: vameid,
                ...vehicle,
                ...vamEntry,
                vamlids: vamEntry.vamlids || vamEntry.vamlid
            }
        })

        setDataSource(newData)
    }

    useEffect(() => {
        setIsLoaded(Object.values(vamEntries).length > 0 || (Object.values(vamEntries).length === 0 && vameids.length === 0 && !style.loading.general));
          if (
              (prevVehicles !== vamEntries)
          ) {
              refreshDataSrc()
          }
    },  [vamEntries])

    const handleImg = (data) => {
        setIsOpen(true);
        setRec(data);
    }
 
    const columns = [
        {
          title: "Vehicle Plate",
          dataIndex: "plate",
          fixed: 'left',
          ...getColumnSearchProps("Vehicle Plate", 'plate', searchTextState, searchedColumnState),
          formatter: ({ plate }) => plate || '-'
        },
        {
            title: "Line Items",
            // dataIndex: 'vamlids',
            render: ({vamlids}) => {
                if(typeof vamlids === 'string') {
                    return <Tag key={vamlids}>{vamLines[vamlids]?.name}</Tag>
                } else if(Array.isArray(vamlids)) {
                    return vamlids?.map(vlid => <Tag key={vlid}>{vamLines[vlid]?.name}</Tag>)
                } else {
                    return '-'
                }
            },
            filters: Object.values(vamLines)
                .map(vam => (
                    {
                        text: vam?.name,
                        value: vam?.vamlid,
                    }
                )),
            onFilter: (value, record) => (record.vamlids || []).includes(value),
            formatter: ({ vamlids = [] }) => Array.isArray(vamlids) && vamlids?.map(v => vamLines[v]?.name)?.join(', ') || vamLines[vamlids]?.name || '-'
        },
        {
            title: "Status",
            dataIndex: 'status',
            render: data => {
                const vam = VAM_ENTRY_STATUS_TITLE[data]
    
                return (
                    <TagLabel tag={vam}/>
                )
            },
            filters: getColumnsFilterTag(VAM_ENTRY_STATUS_TITLE),
            onFilter: (value, record) => record.status === value,
            formatter: (data) => {
                return VAM_ENTRY_STATUS_TITLE[data.status]
            }
        },
        {
            title: "Workshop",
            dataIndex: "workshop",
            ...getColumnSearchProps("Workshop", 'workshop', searchTextState, searchedColumnState),
            render: rec => rec || '-',
            formatter: ({ workshop }) => workshop || '-',
        },
        {
            title: "Total Cost",
            dataIndex: "total_cost",
            ...getColumnSearchProps("Total Cost", 'total_cost', searchTextState, searchedColumnState),
            render: rec => rec || '-',
            formatter: ({ total_cost }) => total_cost || 0,
        },
        {
            title: "Receipt",
            render: (record) => {
            const { photos } = record;

            return photos?.length > 0 ? (
                    <ButtonLink onClick={() => handleImg(photos)}>View</ButtonLink>
                ) : (
                    <span style={{ color: 'gray' }}>View</span>
                );
            },
            formatter: ({ photos = [] }) => photos.length && photos.map(photos => photos.url).join(', ') || "-",
        },
        {
            title: "Remark",
            render: (record) => {
            const { remark } = record;

            return remark ? (
                    <ButtonLink >View</ButtonLink>
                ) : (
                    <span style={{ color: 'gray' }}>View</span>
                );
        },
            formatter: data => data.remark || '-'
        },
        {
            title: "Completion Date",
            dataIndex: 'completedAt',
            sorter: (a, b) => a.completedAt - b.completedAt,
            render: time => parseTime(Number(time)),
            formatter: ({ completedAt }) => parseTime(Number(completedAt)),
        },
        {
            title: "Issued Date",
            dataIndex: 'issuedAt',
            sorter: (a, b) => a.issuedAt - b.issuedAt,
            render: time => parseTime(Number(time)),
            formatter: ({ issuedAt }) => parseTime(Number(issuedAt)),
        },
        {
            title: "Created Date",
            dataIndex: 'updatedAt',
            defaultSortOrder: "descend",
            sorter: (a, b) => a.updatedAt - b.updatedAt,
            render: time => parseTime(Number(time)),
            formatter: ({ updatedAt }) => parseTime(Number(updatedAt)),
        },
        {
            title: "Actions",
            width: 170,
            fixed: 'right',
            render: data => {

                const items = [
                    {
                        key: '1',
                        label: data?.status !== 'VAME200' ? 
                            <span 
                                className="ButtonLink"
                                aria-hidden="true"
                                onClick = {() => ServiceEntryEditHDLR(data)}
                            >
                                Edit
                            </span> : <span style={{ color: 'gray '}}>Edit</span>
                    },
                    {
                        key: '2',
                        label: data?.status !== 'VAME200' ?
                        <ModalStandard 
                            onClick={() => dispatch(REDUX_ACTIONS.v3_vamEntries.cu_vam_entry_request({ ...data, status: 'VAME200', actionAPI: 'U'}))}
                            disabled={data?.status === 'VAME200'}
                            title={'Mark as completed'}
                            content={'You are about to mark this vehicle issue as completed.'}
                            subcontent={`${data?.plate || '-'}`}
                            // icon={<></>}
                        >
                            Mark as completed
                        </ModalStandard>
                        : <span style={{ color: 'gray '}}>Mark as completed</span>
                    }
                ]
                return <Dropdown menu={{ items }} >Actions</Dropdown> 
            }
            }
    ];


    return (
        <>
        <div style={{ display: 'flex', marginBottom: '15px', justifyContent: 'space-between'}}>
            <div>
                <SearchFields />
            </div>
            <div style={{ display: 'flex', gap: '7px' }}>
                <ExportExcelButton
                    sheetData={dataSource}
                    sheetRow={columns.filter(c => !c.isHidden)}
                    filename={`Service Entry - ${TIME.getCurTimeDayJS()}`}
                    sheetName={'Service Entry'}
                />
                <PrimaryButton htmlType={'submit'} onClick={() => navigate (ROUTES.MODULE_ROUTE[COMMON_FEATURES.FEATURE_NAME.CREATE_SERVICE_ENTRY])}>+ Add Service Entry</PrimaryButton>
            </div>
        </div>

            {  !isLoaded ? 
               <Loading /> :
                dataSource.length > 0 ?
               <Table
                    loading={style.loading.vam}
                    columns={columns.filter(c => !c.isHidden)}
                    dataSource={dataSource}
                    pagination={true}
                    scroll={{
                        y: window.innerHeight,
                        x: columns && columns.length * 150
                    }}
                /> 
                :
                    <EmptyIndicator />
            }

            <ModalImage isOpen={isOpen} setIsOpen={setIsOpen} extra={rec} />
        </>
    )
}

export default ServiceEntryPanel